import { Box, Flex } from '@chakra-ui/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FC, Fragment, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { HorizontalTabs } from '@/components/event/HorizontalTabs';
import { Markdown } from '@/components/Markdown';
import { AnnouncementCard } from '@/components/reception/AnnouncementCard';
import { EventInfo } from '@/components/reception/EventInfo';
import { EventTitle } from '@/components/reception/EventTitle';
import { Header } from '@/components/reception/Header';
import { SectionTitle } from '@/components/reception/SectionTitle';
import { OverviewCounter, OverviewCounterSkeleton } from '@/components/ui/core/OverviewCounter/OverviewCounter';
import { GeneralMarkdown } from '@/components/ui/shared/markdowns/GeneralMarkdown/GeneralMarkdown';
import { Announcements } from '@/containers/Reception/Announcements';
import { GroupPreview } from '@/containers/Reception/GroupPreview';
import { SponsorPackage } from '@/containers/Reception/SponsorPackage';
import { useAnalytics } from '@/context/Analytics';
import { FontFamily, FontWeight } from '@/enums/index';
import { fetchAllMenuGroups, FETCH_ALL_MENU_GROUPS_KEY } from '@/fetchers/menu-groups';
import { fetchMenuTabs, FETCH_MENU_TABS_KEY } from '@/fetchers/menu-tabs';
import useBreakpoints from '@/hooks/useBreakpoints';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { queryEventSponsorPackagesWithEventSponsors } from '@/queries/event-sponsor-package';
import { queryThinEvent } from '@/queries/thin-events';
import { BREAKPOINTS } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';

const SectionContainer = styled.div`
  margin-bottom: 72px;
`;

const AnnouncementsContainer = styled.div`
  margin-bottom: 72px;
  margin-top: 25px;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    margin-top: 0;
  }
`;

const Sidebar = styled.div`
  display: none;
  /* position: relative; */
  height: calc(100vh - 4.5rem);
  overflow-y: auto;
  overscroll-behavior-y: contain;
  width: 23rem;
  background-color: white;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    padding: 32px;
    display: block;
  }
`;

const SidebarTitle = styled.h3`
  font-size: 20px;
  line-height: 28px;
`;

const Container = styled.div<IThemeProps>`
  flex: 1;
  height: calc(100vh - 4.5rem);
  overscroll-behavior-y: contain;

  display: flex;

  flex-direction: column;
  overflow: auto;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    border-right: 1px solid ${({ theme }) => theme.legacy.colors.gray80};

    ${AnnouncementsContainer} {
      display: none;
    }
  }
`;

const ContentWrapper = styled.div`
  padding: 0 20px 36px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    padding-left: 32px;
    padding-right: 32px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    padding: 0 72px 60px;
  }
`;

const announcementsItemStyles = css`
  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    flex: 1;
    margin-right: 40px;

    &:last-of-type {
      margin-right: 0;
    }
  }
`;

const HorizontalTabsWrapper = styled.div<IThemeProps>`
  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    display: none;
  }
`;

const ReceptionHorizontalTabs = styled(HorizontalTabs)<IThemeProps>`
  border-bottom: 1px solid ${({ theme }) => theme.legacy.colors.gray80};

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    padding-left: 12px;
  }
`;

const TabItem = styled.div<IThemeProps>`
  font-weight: ${FontWeight.bold};
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGrotesk]};
  font-size: 16px;
  line-height: 18px;
  padding: 8px 16px;
  color: ${({ theme }) => theme.legacy.colors.defaultText};
  cursor: pointer;

  &[aria-current='true'] {
    border-bottom: 2px solid ${({ theme }) => theme.legacy.colors.brand.primary};
    span {
      color: ${({ theme }) => theme.legacy.colors.brand.primary};
      cursor: default;
    }
  }
`;

const StyledMarkdown = styled(Markdown)<IThemeProps>`
  font-size: 16px;
  line-height: 24px;
  text-align: justify;
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily['IBM Plex Sans']};
`;

const eventInfoStyles = css`
  margin-bottom: 44px;
  margin-top: 32px;
  padding: 0 20px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    padding: 0 32px;
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    margin-top: 0;
    padding: 0 72px;
  }
`;

enum Tab {
  Details = 'details',
  Announcments = 'announcements',
}

function stringToTab(value: string): Tab {
  const tabValue = Object.keys(Tab).find((tabName) => Tab[tabName] === value);

  return Tab[tabValue];
}

interface IReceptionContentProps {
  /**
   * Slot for event ticket prompt
   */
  eventTicketSlot: ReactNode;
}

export const ReceptionContent: FC<IReceptionContentProps> = ({ eventTicketSlot }) => {
  const { store } = useStores();
  const { isDesktop } = useBreakpoints();
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(Tab.Details);
  const analytics = useAnalytics();

  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: event } = useResource(queryThinEvent(eventId));

  useEffect(() => {
    if (event) {
      analytics.identify({
        event_name: event.name,
        event_id: event.id,
        event_state: event.state,
      });
    }
  }, [event, analytics]);

  const tabs = [Tab.Details, Tab.Announcments];

  const { data: menuTabs } = useSWR(
    () => (event ? [FETCH_MENU_TABS_KEY, event.id] : null),
    () => fetchMenuTabs(store, event.id, { state: 'peri_event' })
  );

  const { data: menuGroups } = useSWR(
    () => (menuTabs.length > 0 ? [FETCH_ALL_MENU_GROUPS_KEY, menuTabs[0].id] : null),
    () => fetchAllMenuGroups(store, menuTabs[0].id)
  );

  const { data: eventSponsorPackages } = useResources(
    event
      ? queryEventSponsorPackagesWithEventSponsors({
          filters: { event_id: event.id, helpdesk: 'false' },
          sort: 'position',
        })
      : null
  );

  useEffect(() => {
    if (isDesktop) {
      setSelectedTab(Tab.Details);
    }
  }, [isDesktop]);

  function handleTabsChange(tab: string) {
    setSelectedTab(stringToTab(tab));
  }

  const renderContent = () => {
    if (selectedTab === Tab.Details) {
      return (
        <Fragment>
          {event ? (
            <OverviewCounter
              mb={[4, null, 8]}
              items={event?.counters.map(({ name, count }) => ({ name, value: count }))}
            />
          ) : (
            <OverviewCounterSkeleton />
          )}
          <GeneralMarkdown source={event?.description} />
          <SectionContainer>
            <Fragment>
              {menuGroups?.map((menuGroupItem) => {
                if (menuGroupItem.type === 'speakers' || menuGroupItem.type === 'chairs') {
                  return (
                    <div key={menuGroupItem.id}>
                      <SectionTitle>{menuGroupItem.name}</SectionTitle>
                      <GroupPreview menuGroupItem={menuGroupItem} />
                    </div>
                  );
                }
                return null;
              })}
            </Fragment>
          </SectionContainer>
          {eventSponsorPackages && (
            <SectionContainer>
              {eventSponsorPackages
                .filter(({ eventSponsors }) => eventSponsors.length)
                .map((eventSponsorPackage) => (
                  <SponsorPackage key={eventSponsorPackage.id} eventSponsorPackage={eventSponsorPackage} />
                ))}
            </SectionContainer>
          )}
        </Fragment>
      );
    }

    if (selectedTab === Tab.Announcments) {
      return (
        <Flex>
          <AnnouncementsContainer>
            <Announcements eventName={event?.name}>
              {(item) => <AnnouncementCard key={item.createdAt} css={announcementsItemStyles} announcement={item} />}
            </Announcements>
          </AnnouncementsContainer>
        </Flex>
      );
    }

    return null;
  };

  if (!event) return;

  return (
    <Flex>
      <Container>
        <Box w="full">
          <Header event={event} />
        </Box>
        <EventTitle event={event} />
        <EventInfo css={eventInfoStyles} event={event} eventTicketSlot={eventTicketSlot} />
        <HorizontalTabsWrapper>
          <ReceptionHorizontalTabs tabs={tabs} selected={selectedTab} onChange={handleTabsChange}>
            {(tab, selected, onClick) => (
              <TabItem key={tab} aria-current={selected} onClick={() => onClick(tab)}>
                <span>{tab}</span>
              </TabItem>
            )}
          </ReceptionHorizontalTabs>
        </HorizontalTabsWrapper>
        <ContentWrapper>{renderContent()}</ContentWrapper>
      </Container>

      <Sidebar>
        <AnnouncementsContainer>
          <SidebarTitle>{t('announcement.plural')}</SidebarTitle>
          <Announcements eventName={event?.name}>
            {(item) => <AnnouncementCard key={item.createdAt} announcement={item} />}
          </Announcements>
        </AnnouncementsContainer>
      </Sidebar>
    </Flex>
  );
};
